import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ShieldAlert, ShieldCheck, RefreshCw, ArrowRight, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Alerta automática de clientes desincronizados (salto de mesociclo).
 * Consulta /trainer/clients/desync-check (admin ve todos, trainer los suyos).
 *
 * Props:
 *  - basePath: ruta base del detalle de cliente ("/admin/clients" o "/trainer/clients")
 */
const DesyncAlertCard = ({ basePath = '/admin/clients' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDesync = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/trainer/clients/desync-check`);
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDesync();
  }, [fetchDesync]);

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="desync-alert-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Verificando sincronización de macros...
      </div>
    );
  }

  if (!data) return null;

  const hasProblems = data.desync_count > 0 || data.warning_count > 0;

  // Todo OK → línea verde discreta
  if (!hasProblems) {
    return (
      <div
        className="flex items-center justify-between rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-4 py-2.5"
        data-testid="desync-alert-ok"
      >
        <div className="flex items-center gap-2 text-sm text-emerald-300">
          <ShieldCheck className="h-4 w-4" />
          Sincronización de macros OK · {data.total_checked} clientes verificados
        </div>
        <Button variant="ghost" size="sm" onClick={fetchDesync} className="h-7 px-2 text-muted-foreground" data-testid="desync-refresh-btn">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
    );
  }

  const allEntries = [...(data.desync_details || []), ...(data.warnings || [])];

  return (
    <Card className="border-red-500/40 bg-red-950/20" data-testid="desync-alert-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/20">
              <ShieldAlert className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-semibold text-red-300">
                {data.desync_count > 0
                  ? `${data.desync_count} cliente${data.desync_count !== 1 ? 's' : ''} desincronizado${data.desync_count !== 1 ? 's' : ''}`
                  : `${data.warning_count} aviso${data.warning_count !== 1 ? 's' : ''} de sincronización`}
              </h3>
              <p className="text-xs text-muted-foreground">
                Detectado antes de que el cliente lo note. Usa "Posición en el Macro" para reposicionar.
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchDesync} className="h-7 px-2 text-muted-foreground" data-testid="desync-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        <div className="space-y-2">
          {allEntries.map((entry) => (
            <div
              key={entry.client_id}
              className="flex items-center justify-between gap-3 rounded-md border border-border bg-background/40 px-3 py-2"
              data-testid={`desync-entry-${entry.client_id}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-foreground truncate">{entry.client_name}</span>
                  <Badge
                    variant="outline"
                    className={entry.severity === 'critical'
                      ? 'border-red-500/50 text-red-400 text-[10px] px-1.5 py-0'
                      : 'border-amber-500/50 text-amber-400 text-[10px] px-1.5 py-0'}
                  >
                    {entry.severity === 'critical' ? 'CRÍTICO' : 'AVISO'}
                  </Badge>
                </div>
                <ul className="text-xs text-muted-foreground mt-0.5 space-y-0.5">
                  {entry.issues.map((iss, i) => (
                    <li key={i} className="truncate">• {iss.detail}</li>
                  ))}
                </ul>
              </div>
              <Link to={`${basePath}/${entry.client_id}`} className="flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10 h-8"
                  data-testid={`desync-fix-btn-${entry.client_id}`}
                >
                  Reposicionar
                  <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default DesyncAlertCard;
